import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip, Warning } from '../../components/LessonUI';

const JSTypeCoercion = () => {
    const coercionBasicsCode = `// Implicit coercion (automatic engine behavioral)
console.log('5' + 3);      // '53' (number  string)
console.log('5' - 3);      // 2 (string  number)
console.log(true + 1);     // 2 (boolean  number, true becomes 1)

// Explicit conversion (manual engineering intent)
console.log(Number('5'));  // 5
console.log(String(123));  // '123'
console.log(Boolean(0));   // false`;

    const stringCoercionCode = `// The "+" operator: String Concatenation vs Addition
console.log('Value: ' + 42);        // 'Value: 42'
console.log('Empty: ' + null);      // 'Empty: null'

// Complex types to string
// JavaScript calls the internally defined .toString() or .valueOf()
console.log('Array: ' + [1, 2, 3]);        // 'Array: 1,2,3'
console.log('Object: ' + { a: 1 });        // 'Object: [object Object]'`;

    const numberCoercionCode = `// Mathematical operators force numeric coercion
console.log('10' / 2);     // 5
console.log('10' % 3);     // 1

// The Unary Plus: A concise way to cast to Number
const priceInput = '49.99';
const price = +priceInput;
console.log(typeof price); // "number"

// Pitfalls: Non-numeric strings
console.log(+'hello');     // NaN (Not a Number)
console.log(Number(undefined)); // NaN`;

    const equalityCode = `// Loose Equality (==): Allows Coercion
console.log(5 == '5');         // true
console.log(0 == false);       // true
console.log(null == undefined); // true

// Strict Equality (===): No Coercion (Always Preferred)
console.log(5 === '5');         // false
console.log(0 === false);       // false
console.log(null === undefined); // false`;

    const logicCoercionCode = `// Truthy and Falsy in Conditionals
const items = [];
if (items.length) {
    // This won't run because 0 is falsy
}

// Logical OR (||) for defaults
const speed = userSpeed || 50; // Problem: if speed is 0, it becomes 50!

// Nullish Coalescing (??) - Modern Solution
const actualSpeed = userSpeed ?? 50; // Only triggers on null/undefined`;

    const defensiveCode = `// Defensive Programming: Ensuring Type Safety
function processPayment(amount) {
    const numericAmount = Number(amount);
    
    if (Number.isNaN(numericAmount)) {
        throw new Error('Invalid numeric input');
    }
    
    return numericAmount.toFixed(2);
}

console.log(processPayment('100.5')); // "100.50"
// console.log(processPayment('abc')); // Throws Error`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">JavaScript Fundamentals</div>
                <h1>Type Coercion & Conversion</h1>
                <p className="hero-lead">
                    Master the mechanics of JavaScript's type transitions. Learn the difference between
                    implicit coercion and explicit conversion, and how to write type-safe code in a dynamic environment.
                </p>
            </div>

            <div className="content-section">
                <h2>The Nature of Type Coercion</h2>
                <p>
                    Because JavaScript is a <strong>loosely typed</strong> language, the engine will
                    frequently attempt to convert values between types automatically to complete an
                    operation. This is known as <strong>Implicit Coercion</strong>.
                </p>
                <ProTip><strong> Engineer's Rule:</strong> While coercion can lead to shorter code, it often
                    creates "silent bugs." Modern professional development prioritizes
                    <strong>Explicit Conversion</strong>making your intentions clear to both the engine
                    and other developers.</ProTip>
                <CodeBlock language="javascript" code={coercionBasicsCode} />
            </div>

            <div className="content-section">
                <h2>Implicit Coercion Rules</h2>

                <h3>1. String Coercion with the "+" Operator</h3>
                <p>
                    When the <code>+</code> operator sees a string, it prioritizes <strong>concatenation</strong>
                    over mathematical addition. Any other type involved will be converted to its string
                    representation.
                </p>
                <CodeBlock language="javascript" code={stringCoercionCode} />

                <h3>2. Numeric Coercion</h3>
                <p>
                    Operators like <code>-</code>, <code>*</code>, <code>/</code>, and <code>%</code> only
                    work with numbers. If they encounter other types, they force a conversion to Number.
                </p>
                <CodeBlock language="javascript" code={numberCoercionCode} />
            </div>

            <div className="content-section">
                <h2>The Equality Comparison Algorithm</h2>
                <p>
                    One of the most common sources of confusion in JavaScript is the difference between
                    loose (<code>==</code>) and strict (<code>===</code>) equality.
                </p>
                <ul>
                    <li><strong>Loose Equality (<code>==</code>):</strong> Triggers the "Abstract Equality Comparison Algorithm," which attempts to coerce the operands to a common type before comparing.</li>
                    <li><strong>Strict Equality (<code>===</code>):</strong> Does NOT allow coercion. If the types are different, the result is immediately <code>false</code>.</li>
                </ul>
                <CodeBlock language="javascript" code={equalityCode} />
                <Warning><strong> Caution:</strong> Using <code>==</code> can lead to bizarre outcomes like
                    <code>[] == ![]</code> returning <code>true</code>. Professional style guides
                    (like Airbnb's) strictly forbid <code>==</code>.</Warning>
            </div>

            <div className="content-section">
                <h2>Truthy vs Falsy Values</h2>
                <p>
                    In a logical context (like an <code>if</code> statement), JavaScript coerces
                    values into booleans. Most values are <strong>Truthy</strong>, but there are
                    exactly 8 <strong>Falsy</strong> values that evaluate to <code>false</code>:
                </p>
                <div className="falsy-grid">
                    <div className="falsy-item"><code>false</code></div>
                    <div className="falsy-item"><code>0</code></div>
                    <div className="falsy-item"><code>-0</code></div>
                    <div className="falsy-item"><code>0n</code> (BigInt)</div>
                    <div className="falsy-item"><code>""</code> (Empty String)</div>
                    <div className="falsy-item"><code>null</code></div>
                    <div className="falsy-item"><code>undefined</code></div>
                    <div className="falsy-item"><code>NaN</code></div>
                </div>
                <CodeBlock language="javascript" code={logicCoercionCode} />
            </div>

            <div className="content-section">
                <h2>Defensive Programming: Explicit Casting</h2>
                <p>
                    To write resilient enterprise applications, you should use built-in constructors
                    to explicitly cast values. This ensures that your business logic never operates
                    on unexpected types.
                </p>
                <table className="comparison-table">
                    <thead>
                        <tr>
                            <th>Target Type</th>
                            <th>Best Practice Methods</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Number</strong></td>
                            <td><code>Number(val)</code>, <code>parseInt(val, 10)</code>, <code>+val</code></td>
                        </tr>
                        <tr>
                            <td><strong>String</strong></td>
                            <td><code>String(val)</code>, <code>val.toString()</code>, Template Literals</td>
                        </tr>
                        <tr>
                            <td><strong>Boolean</strong></td>
                            <td><code>Boolean(val)</code>, <code>!!val</code></td>
                        </tr>
                    </tbody>
                </table>
                <CodeBlock language="javascript" code={defensiveCode} />
            </div>

            <KeyFacts><ul>
                    <li><strong>Always use <code>===</code>:</strong> Prevent accidental type juggling.</li>
                    <li><strong>Favor Explicit Conversion:</strong> Use <code>Number()</code> or <code>String()</code>.</li>
                    <li><strong>Radix in parseInt:</strong> Always include the base, e.g., <code>parseInt(val, 10)</code>.</li>
                    <li><strong>Logical Coalescing:</strong> Use <code>??</code> instead of <code>||</code> for safer default values.</li>
                    <li><strong>Strict NaN Check:</strong> Use <code>Number.isNaN()</code> rather than the global <code>isNaN()</code>.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSTypeCoercion;


